const API_BASE = '/api';

// Listeners that want to know when authentication state changes (e.g. so
// the auth context can drop the user when a refresh fails). Decoupled
// from React so api.ts has no React dependency.
type AuthChangeListener = (state: 'authenticated' | 'unauthenticated') => void;
const authListeners = new Set<AuthChangeListener>();

export function onAuthChange(listener: AuthChangeListener): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

function emitAuthChange(state: 'authenticated' | 'unauthenticated') {
  for (const listener of authListeners) {
    try {
      listener(state);
    } catch (err) {
      console.error('auth listener threw', err);
    }
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    emitAuthChange('unauthenticated');
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

export interface Report {
  id: string;
  key: string;
  lastModified: string;
  size: number;
  friendlyName?: string;
  description?: string;
  uploadedBy?: string;
  reportDate?: string;
  docType?: string;
  businessUnit?: string;
  year?: number;
  archived?: boolean;
}

export interface AccountRequest {
  email: string;
  requestedAt: string;
  name: string;
  organization: string;
  reason: string;
  status: string;
  requestedAocReportIds?: string[];
  requestedDocuments?: { reportId: string; accessMode: 'view' | 'download' }[];
  complianceRegime?: string;
  businessUnit?: string;
  reRequestPending?: boolean;
  requestType?: 'add' | 'replace';
  existingPolicy?: AccessPolicy;
}

export interface AccessPolicy {
  approvedTypes: string[];
  approvedTypesYear?: number | null;
  rocReportIds: string[];
  approvedReportIds: string[];
  accessMode?: 'view' | 'download';
  expiresAt?: string | null;
}

export interface PortalUser {
  email: string;
  name?: string;
  organization?: string;
  authMethod?: string;
  requestStatus?: string;
  requestedAt?: string;
  reason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  canDownloadRoc?: boolean;
  accessPolicy?: AccessPolicy;
  requestedAocReportIds?: string[];
  isAdmin?: boolean;
  adminAlias?: string;
}

export interface AuditLog {
  pk: string;
  sk: string;
  userId?: string;
  userAlias?: string;
  authMethod?: string;
  eventType?: string;
  timestamp: string;
  outcome?: string;
  sourceIp?: string;
  affectedResource?: string;
  user?: string;
  action?: string;
  reportId?: string;
  targetEmail?: string;
  targetAlias?: string;
  reason?: string;
}

export interface NotificationSettings {
  denyEmailIdprism: boolean;
  denyEmailMidway: boolean;
  approveEmailIdprism: boolean;
  approveEmailMidway: boolean;
}

export interface WhitelistEntry {
  domain: string;
  approvedTypes: string[];
  enabled: boolean;
  addedBy: string;
  addedAt: string;
}

export const api = {
  // External user self-service auth (IdPrism)
  signup: (data: { email: string; password: string; name: string; organization: string; reason?: string; requestedAocReportIds?: string[]; phoneNumber?: string; ndaAcknowledged?: boolean }) =>
    request<{ message: string; email: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  idprismSignup: (data: { email: string; name: string; organization: string; reason?: string; requestedAocReportIds?: string[]; phoneNumber?: string; ndaAcknowledged?: boolean }) =>
    request<{ message: string; email: string; cupidUrl: string; nextStep: string }>('/auth/idprism/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  confirmSignup: (data: { email: string; code: string }) =>
    request<{ message: string }>('/auth/confirm', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resendConfirmation: (email: string) =>
    request<{ message: string }>('/auth/resend-confirmation', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  forgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (data: { email: string; code: string; newPassword: string }) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Server-side logout: clears the HttpOnly session cookie.
  logout: async (): Promise<void> => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.error('Logout request failed', err);
    }
  },

  // Public AOC catalog — unauthenticated. Used by the signup page.
  listPublicAocs: () =>
    request<{ aocs: { id: string; friendlyName: string; reportDate: string; docType?: string; businessUnit?: string }[] }>('/public/aocs'),

  // Current user info
  me: () => request<{
    email: string;
    name: string;
    alias: string | null;
    authMethod: string;
    canDownload: boolean;
    canDownloadRoc: boolean;
    accessMode?: 'view' | 'download';
    isAdmin: boolean;
    requestStatus?: 'PENDING' | 'APPROVED' | 'DENIED';
    reviewerNotes?: string;
    requestedAt?: string;
    reviewedAt?: string;
    notifyNewDocuments?: boolean;
    pendingReRequest?: boolean;
    pendingRequestDetails?: {
      requestedDocuments: { reportId: string; accessMode: 'view' | 'download' }[];
      requestedAocReportIds: string[];
    };
  }>('/me'),

  // Update the AOCs the current user wants access to (only works while PENDING)
  updateMyRequestedAocs: (requestedAocReportIds: string[]) =>
    request<{ message: string; requestedAocReportIds: string[] }>('/me/requested-aocs', {
      method: 'PATCH',
      body: JSON.stringify({ requestedAocReportIds }),
    }),

  // Submit a new access request (works from any state: approved, denied, pending)
  reRequestAccess: (data: {
    reason?: string;
    requestedAocReportIds?: string[];
    requestedDocuments?: { reportId: string; accessMode: 'view' | 'download' }[];
    complianceRegime?: string;
    businessUnit?: string;
    requestType?: 'add' | 'replace';
  }) =>
    request<{ message: string; requestedAt: string }>('/me/re-request', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update notification preferences
  updateNotifications: (notifyNewDocuments: boolean) =>
    request<{ message: string; notifyNewDocuments: boolean }>('/me/notifications', {
      method: 'PATCH',
      body: JSON.stringify({ notifyNewDocuments }),
    }),

  // Reports
  listReports: () => request<{ reports: Report[] }>('/reports'),

  downloadReport: (reportId: string) =>
    request<{ url: string; reportId: string }>(`/reports/${encodeURIComponent(reportId)}`),

  viewReport: (reportId: string) =>
    request<{ url: string; reportId: string }>(`/reports/${encodeURIComponent(reportId)}/view`),

  uploadReport: (data: { reportId: string; friendlyName?: string; description?: string; reportDate?: string; docType?: string; year?: number; businessUnit?: string }) =>
    request<{ uploadUrl: string; reportId: string }>('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  editReport: (
    reportId: string,
    data: { friendlyName?: string; description?: string; reportDate?: string; docType?: string; year?: number; businessUnit?: string },
  ) =>
    request<{ message: string; reportId: string }>(`/reports/${encodeURIComponent(reportId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteReport: (reportId: string) =>
    request<{ message: string }>(`/reports/${encodeURIComponent(reportId)}`, {
      method: 'DELETE',
    }),

  archiveReport: (reportId: string, archived: boolean) =>
    request<{ message: string; reportId: string; archived: boolean }>(`/reports/${encodeURIComponent(reportId)}`, {
      method: 'PUT',
      body: JSON.stringify({ archived }),
    }),

  // Account requests (admin)
  listPendingRequests: () => request<{ requests: AccountRequest[] }>('/account-requests'),

  // Users (admin)
  listUsers: () =>
    request<{ users: PortalUser[]; truncated: boolean; total: number }>('/users'),

  disableUser: (email: string) =>
    request<{ message: string; email: string }>(
      `/users/${encodeURIComponent(email)}/disable`,
      { method: 'POST' },
    ),

  enableUser: (email: string) =>
    request<{ message: string; email: string }>(
      `/users/${encodeURIComponent(email)}/enable`,
      { method: 'POST' },
    ),

  deleteUser: (email: string) =>
    request<{ message: string; email: string }>(
      `/users/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    ),

  setRocAccess: (email: string, allow: boolean) =>
    request<{ message: string; email: string; canDownloadRoc: boolean }>(
      `/users/${encodeURIComponent(email)}/roc-access`,
      { method: 'PATCH', body: JSON.stringify({ allow }) },
    ),

  setAccessPolicy: (email: string, policy: AccessPolicy) =>
    request<{ message: string; email: string; accessPolicy: AccessPolicy }>(
      `/users/${encodeURIComponent(email)}/access-policy`,
      { method: 'PATCH', body: JSON.stringify(policy) },
    ),

  setUserProfile: (email: string, data: { name?: string; organization?: string }) =>
    request<{ message: string; email: string }>(
      `/users/${encodeURIComponent(email)}/profile`,
      { method: 'PATCH', body: JSON.stringify(data) },
    ),

  promoteAdmin: (alias: string) =>
    request<{ message: string }>(`/admins/${encodeURIComponent(alias)}`, { method: 'PUT' }),

  demoteAdmin: (alias: string) =>
    request<{ message: string }>(`/admins/${encodeURIComponent(alias)}`, { method: 'DELETE' }),

  handleRequest: (data: {
    email: string;
    requestedAt: string;
    action: 'APPROVED' | 'DENIED';
    reviewerNotes?: string;
    accessPolicy?: AccessPolicy;
  }) =>
    request<{ message: string; email: string }>(`/account-requests/${encodeURIComponent(data.email)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Audit
  getAuditLogs: (lastKey?: string) =>
    request<{ logs: AuditLog[]; nextKey: string | null; hasMore: boolean }>(
      `/audit${lastKey ? `?lastKey=${encodeURIComponent(lastKey)}` : ''}`,
    ),

  // Admin notification settings
  getSettings: () =>
    request<{ settings: NotificationSettings; whitelist: WhitelistEntry[] }>('/settings'),

  updateSettings: (settings: Partial<NotificationSettings>) =>
    request<{ settings: NotificationSettings }>('/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),

  // Domain whitelist
  addWhitelistDomain: (domain: string, approvedTypes: string[]) =>
    request<{ whitelist: WhitelistEntry[] }>('/settings/whitelist', {
      method: 'PUT',
      body: JSON.stringify({ domain, approvedTypes }),
    }),

  updateWhitelistDomain: (domain: string, data: { approvedTypes?: string[]; enabled?: boolean }) =>
    request<{ whitelist: WhitelistEntry[] }>('/settings/whitelist', {
      method: 'PATCH',
      body: JSON.stringify({ domain, ...data }),
    }),

  removeWhitelistDomain: (domain: string) =>
    request<{ whitelist: WhitelistEntry[] }>('/settings/whitelist', {
      method: 'DELETE',
      body: JSON.stringify({ domain }),
    }),

  // Debug logs
  getDebugLogs: (params: { minutes?: number; filter?: string; nextToken?: string }) => {
    const qs = new URLSearchParams();
    if (params.minutes) qs.set('minutes', String(params.minutes));
    if (params.filter) qs.set('filter', params.filter);
    if (params.nextToken) qs.set('nextToken', params.nextToken);
    return request<{
      events: { timestamp: number; message: string; stream: string }[];
      nextToken: string | null;
      logGroup: string;
      timeRange: { startTime: number; endTime: number; minutes: number };
    }>(`/debug/logs?${qs.toString()}`);
  },
};
